import {
  Avatar,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import React, { useState } from "react";
import { PiCaretDownBold, PiNotePencilDuotone, PiGlobeDuotone, PiLockDuotone, PiShieldDuotone } from "react-icons/pi";
import { CommentsPolicy, createPortalPost, FeedPost } from "@lubbezposrednio-frontend/core";
import { VerifiedBadge } from "../../components/VerifiedBadge";
import { GLASS, drgba } from "../../components/glassUi";

const POLICIES: { value: CommentsPolicy; label: string; icon: React.ElementType }[] = [
  { value: "all", label: "Komentują wszyscy", icon: PiGlobeDuotone },
  { value: "verified", label: "Tylko zweryfikowani", icon: PiShieldDuotone },
  { value: "none", label: "Komentowanie wyłączone", icon: PiLockDuotone }
];

// Kompozer postów portalowych — pasek wyzwalający MODAL (jak w grupach).
export const PortalComposer: React.FC<{ name?: string; avatarUrl?: string; verified?: boolean; onPosted: (p: FeedPost) => void }> = ({ name, avatarUrl, verified, onPosted }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [body, setBody] = useState("");
  const [policy, setPolicy] = useState<CommentsPolicy>("all");
  const [posting, setPosting] = useState(false);
  const current = POLICIES.find(p => p.value === policy)!;

  const submit = async () => {
    const value = body.trim();
    if (!value) return;
    setPosting(true);
    try {
      const { data } = await createPortalPost(value, policy);
      onPosted(data as FeedPost);
      setBody("");
      setPolicy("all");
      onClose();
      toast({ title: "Opublikowano post", status: "success", isClosable: true });
    } catch {
      toast({ title: "Nie udało się opublikować", status: "error", isClosable: true });
    } finally {
      setPosting(false);
    }
  };

  return (
    <>
      {/* Pasek wyzwalający (jak w grupach) */}
      <Box borderRadius="20px" p="12px 14px" sx={GLASS}>
        <HStack spacing="11px">
          <Avatar size="sm" boxSize="40px" name={name || "?"} src={avatarUrl} bg="primary" color="white" />
          <Flex
            as="button"
            onClick={onOpen}
            flex="1"
            align="center"
            h="42px"
            px="16px"
            bg="rgba(255,255,255,0.6)"
            border="1px solid rgba(24,12,48,0.06)"
            borderRadius="full"
            color="#8a8397"
            fontSize="14.5px"
            _hover={{ bg: drgba("#430098", 0.06) }}
          >
            Napisz post do społeczności…
          </Flex>
          <Button onClick={onOpen} leftIcon={<Icon as={PiNotePencilDuotone} />} bg="primary" color="white" borderRadius="12px" fontWeight="700" flexShrink={0} display={{ base: "none", sm: "inline-flex" }} _hover={{ bg: "#360079" }}>
            Post
          </Button>
        </HStack>
      </Box>

      {/* Modal tworzenia (jak w grupach) */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay bg="rgba(20,0,45,0.55)" backdropFilter="blur(3px)" />
        <ModalContent borderRadius="20px" overflow="hidden">
          <ModalHeader textAlign="center" borderBottom="1px solid rgba(67,0,152,0.08)" fontSize="17px" color="primary" fontWeight="800">
            Nowy post portalowy
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pt="18px">
            <HStack spacing="11px" mb="14px">
              <Avatar size="sm" boxSize="42px" name={name || "?"} src={avatarUrl} bg="primary" color="white" />
              <Box>
                <HStack spacing="4px">
                  <Text fontWeight="800" color="primary" fontSize="15px">{name || "Ty"}</Text>
                  {verified && <VerifiedBadge size="14px" />}
                </HStack>
                <Text fontSize="12px" color="gray.400">Publikujesz publicznie w Społeczności</Text>
              </Box>
            </HStack>
            <Textarea
              autoFocus
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="Co słychać? Podziel się z całą społecznością…"
              minH="150px"
              border="none"
              fontSize="17px"
              p="0"
              resize="none"
              _focusVisible={{ boxShadow: "none" }}
              maxLength={5000}
            />
          </ModalBody>
          <ModalFooter flexDir="column" alignItems="stretch" gap="10px" borderTop="1px solid rgba(67,0,152,0.06)">
            <Flex justify="space-between" align="center" w="100%" gap="10px" wrap="wrap">
              <Menu>
                <MenuButton as={Button} size="sm" variant="outline" borderColor="rgba(67,0,152,0.18)" color="gray.600" borderRadius="10px" fontWeight="700" leftIcon={<Icon as={current.icon} />} rightIcon={<Icon as={PiCaretDownBold} boxSize="12px" />}>
                  {current.label}
                </MenuButton>
                <MenuList minW="220px" borderRadius="12px">
                  {POLICIES.map(p => (
                    <MenuItem key={p.value} icon={<Icon as={p.icon} />} onClick={() => setPolicy(p.value)} fontWeight={p.value === policy ? "700" : "500"} color={p.value === policy ? "primary" : "gray.700"}>
                      {p.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
              <Text fontSize="12px" color="gray.400">{body.length}/5000</Text>
            </Flex>
            <Button onClick={submit} isLoading={posting} isDisabled={!body.trim()} w="100%" bg="primary" color="white" borderRadius="12px" fontWeight="800" size="lg" _hover={{ bg: "#360079" }}>
              Opublikuj
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
